import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Calendar, Car, Users, Bell, LogOut, Menu, BellOff } from "lucide-react";

const UserDashboard = () => {
  
  const [bookings, setBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const quotes = [
    { text: "The best time to find parking is before you need it.", author: "Smart Commuter" },
    { text: "Carpool today, cleaner air tomorrow.", author: "Green Initiative" },
    { text: "Every shared ride is a step towards a sustainable future.", author: "Eco Friendly" },
    { text: "Park smart, live smart.", author: "Urban Wisdom" },
    { text: "Good parking spots come to those who plan ahead.", author: "City Navigator" },
    { text: "Share the ride, share the joy.", author: "Community Spirit" },
  ];

  const [currentQuote] = useState(() => quotes[Math.floor(Math.random() * quotes.length)]);

  useEffect(() => {
    fetchData();
  }, []);

  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchData = async () => {
    try {
      const today = getLocalDateString(new Date());
      const [bookingsRes, notifsRes] = await Promise.all([
        axios.get(`${API}/parking/bookings?date=${today}`),
        axios.get(`${API}/notifications`),
      ]);
      setBookings(bookingsRes.data);
      setNotifications(notifsRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/parking/bookings/${bookingId}`);
      toast.success("Booking cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel booking");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="user-dashboard">
      {/* Floating Navigation */}
      <nav className="floating-nav">
        <div className="flex items-center justify-between gap-6">
          <button onClick={() => navigate("/dashboard")} className="text-[#1A3C34] font-bold text-xl" style={{width:'120px'}}>
            <img src="../parkioo_logo.png" alt="Parkioo Logo" className="h-50 w-full object-contain p-2" />
          </button>
          <div className="flex gap-4">
            <Button
              data-testid="nav-dashboard"
              onClick={() => navigate("/dashboard")}
              variant="ghost"
              className="text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
            >
              Dashboard
            </Button>
            <Button
              data-testid="nav-booking"
              onClick={() => navigate("/booking")}
              variant="ghost"
              className="text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
            >
              Book Parking
            </Button>
            <Button
              data-testid="nav-vehicles"
              onClick={() => navigate("/vehicles")}
              variant="ghost"
              className="text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
            >
              Vehicles
            </Button>
            <Button
              data-testid="nav-carpool"
              onClick={() => navigate("/carpool")}
              variant="ghost"
              className="text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
            >
              Carpool
            </Button>
            <Button
              data-testid="nav-my-profile"
              onClick={() => navigate("/my-profile")}
              variant="ghost"
              className="text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
            >
              My Profile
            </Button>
            <div className="relative">
              <Button
                data-testid="nav-notifications"
                onClick={() => setShowNotifications(!showNotifications)}
                variant="ghost"
                className="text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full relative"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#FF6B35] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>
            </div>
            <Button
              data-testid="nav-logout"
              onClick={handleLogout}
              variant="ghost"
              className="text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
            >
              <LogOut size={20} />
            </Button>
          </div>
        </div>
      </nav>

      {/* Notifications Panel */}
      {showNotifications && (
        <div className="fixed top-24 right-6 w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 max-h-96 overflow-y-auto" data-testid="notifications-panel">
          <div className="p-6">
            <h3 className="text-xl font-bold text-[#1A3C34] mb-4">Notifications</h3>
            {notifications.length === 0 ? (
              <p className="text-gray-600">No notifications</p>
            ) : (
              <div className="space-y-3">
                {notifications.map((notif) => (
                  <div
                    key={notif._id}
                    data-testid={`notification-${notif._id}`}
                    className={`p-4 rounded-lg border ${
                      notif.read ? "bg-gray-50 border-gray-200" : "bg-[#F5F2EB] border-[#1A3C34]"
                    }`}
                  >
                    <p className="text-sm text-gray-800">{notif.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(notif.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="pt-32 pb-12 px-6 max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-12 fade-in">
          <h1 className="text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-4">
            Welcome back, {user.name}!
          </h1>
          <p className="text-xl text-gray-600">Here's what's happening with your parking.</p>
          
          {/* Quote Card */}
          <Card className="mt-6 p-6 bg-gradient-to-r from-[#F5F2EB] to-white border-l-4 border-[#FF6B35]">
            <p className="text-lg italic text-[#1A3C34]">"{currentQuote.text}"</p>
            <p className="text-sm text-gray-500 mt-2">— {currentQuote.author}</p>
          </Card>
        </div>

        {/* Stats Grid */}
        <div className="bento-grid mb-12">
          <Card className="p-8 bg-gradient-to-br from-[#1A3C34] to-[#2D5F52] text-white border-none">
            <Calendar className="mb-4" size={32} />
            <p className="text-sm opacity-90 mb-2">Active Bookings</p>
            <p className="text-4xl font-bold">{bookings.filter(b =>b.status !== "cancelled" && b.status !== "completed").length}</p>
          </Card>

          <Card className="p-8 bg-[#F5F2EB] border-none">
            <BellOff className="mb-4 text-[#1A3C34]" size={32} />
            <p className="text-sm text-gray-600 mb-2">Cancelled Bookings</p>
            <p className="text-4xl font-bold text-[#1A3C34]">{bookings.filter(b => b.status === "cancelled").length}</p>
          </Card>

          <Card className="p-8 bg-white border border-gray-100 col-span-1 md:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#1A3C34]">Quick Actions</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Button
                data-testid="quick-book-parking"
                onClick={() => navigate("/booking")}
                className="bg-[#FF6B35] hover:bg-[#E55A28] text-white rounded-xl py-6"
              >
                <Calendar className="mr-2" size={20} />
                Book Parking
              </Button>
              <Button
                data-testid="quick-carpool"
                onClick={() => navigate("/carpool")}
                className="bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-xl py-6"
              >
                <Users className="mr-2" size={20} />
                Find Carpool
              </Button>
            </div>
          </Card>
        </div>

        {/* Active Bookings */}
        <div className="fade-in">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-[#1A3C34]">Your Bookings</h2>
            <Button
              data-testid="new-booking-btn"
              onClick={() => navigate("/booking")}
              className="bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full px-6"
            >
              New Booking
            </Button>
          </div>

          {bookings.length === 0 ? (
            <Card className="p-12 text-center">
              <Calendar className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-600 text-lg mb-4">No active bookings</p>
              <Button
                data-testid="book-now-empty-state"
                onClick={() => navigate("/booking")}
                className="bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full px-6"
              >
                Book Now
              </Button>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bookings.map((booking) => (
                <Card key={booking._id} data-testid={`booking-${booking._id}`} className="p-6 border border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-2xl font-bold text-[#1A3C34]">
                        Slot {booking.slot_number || "N/A"}
                      </p>
                      <p className="text-sm text-gray-600">{booking.floor || ""}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      booking.status === "reserved" ? "bg-yellow-100 text-yellow-800" :
                      booking.status === "checked_in" ? "bg-blue-100 text-blue-800" :
                      booking.status === "completed" ? "bg-green-100 text-green-800" :
                      booking.status === "cancelled" ? "bg-red-100 text-red-800" :
                      "bg-gray-100 text-gray-800"
                    }`}>
                      {booking.status}
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <p className="text-gray-600">
                      <span className="font-medium">Date:</span> {booking.date}
                    </p>
                    {booking.time_slot && (
                      <p className="text-gray-600">
                        <span className="font-medium">Time:</span> {booking.time_slot.start_time} -{" "}
                        {booking.time_slot.end_time}
                      </p>
                    )}
                    {booking.vehicle && (
                      <p className="text-gray-600">
                        <span className="font-medium">Vehicle:</span> {booking.vehicle.make}{" "}
                        {booking.vehicle.model}
                      </p>
                    )}
                  </div>
                  {booking.status !== "CANCELLED" || booking.status !== "cancelled" && (  
                  <Button
                    data-testid={`cancel-booking-${booking._id}`}
                    onClick={() => handleCancelBooking(booking._id)}
                    variant="outline"
                    className="w-full border-red-300 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Cancel Booking
                  </Button>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;